import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Box,
  Button,
  ButtonText,
  Center,
  Heading,
  Spinner,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import {
  RouteProp,
  useIsFocused,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useTranslation } from 'react-i18next';

import { useGetUserBookings } from '@/common/api/endpoints/appointments/appointments';
import type { Booking } from '@/common/api/models/booking';
import { useAuthStore } from '@/common/auth/store';
import type { StudentRouteParamList } from '@/common/navigation/RoleNavigations/Student/types';
import { createLiveKitJoinHandle } from '@/common/utils/oralExam/liveKitJoin';
import { isLiveKitBooking } from '@/common/utils/oralExam/liveKitSlot';
import { normalizeSlotMeetingSlotId } from '@/common/utils/oralExam/slotMeetingRoute';
import { OralExamLiveSession } from '@/student/module/components/OralExamLiveSession';
import { resolveSlotMeetingBooking } from '@/student/module/hooks/useEventTaskSlots';

type SlotMeetingRouteProp = RouteProp<StudentRouteParamList, 'SlotMeetingScreen'>;
type SlotMeetingNavProp = NativeStackNavigationProp<StudentRouteParamList, 'SlotMeetingScreen'>;

/**
 * SlotMeetingScreen
 * -----------------
 * Deep-link entry for slot-centric LiveKit join URLs (`meeting/slot/:slotId`).
 * Resolves the authenticated student's booking for the slot and mounts the LiveKit room.
 */
export function SlotMeetingScreen() {
  const { t } = useTranslation();
  const route = useRoute<SlotMeetingRouteProp>();
  const navigation = useNavigation<SlotMeetingNavProp>();
  const isFocused = useIsFocused();
  const {
    slotId: rawSlotId,
    bookingId,
    courseId: practiceCourseId,
    moduleId: practiceModuleId,
    taskId: practiceTaskId,
    sessionId: practiceSessionId,
  } = route.params;
  // Linking used to bake missing params as the string "undefined" (FLARE-8E).
  const slotId = normalizeSlotMeetingSlotId(rawSlotId);

  const userId = useAuthStore((s) => s.user?.id);
  const isReservationlessPractice = Boolean(
    practiceCourseId && practiceModuleId && practiceTaskId && (practiceSessionId || bookingId),
  );

  const {
    data: bookingsResp, isPending: bookingsLoading, isFetching, refetch,
  } = useGetUserBookings(
    userId ?? '',
    undefined,
    {
      query: {
        enabled: Boolean(userId) && !isReservationlessPractice,
        refetchOnMount: 'always',
      },
    },
  );

  const bookingList = bookingsResp?.data?.data ?? [];
  const resolvedBooking = isReservationlessPractice
    ? undefined
    : resolveSlotMeetingBooking(bookingList, { slotId: slotId ?? '', bookingId });

  // Integrity void cancels the booking. The list refetch then drops it, which
  // used to unmount OralExamLiveSession onto slotMeeting.noBooking. Keep the
  // last resolved session so run-state can show SessionVoidTechnicalScreen.
  const [heldBooking, setHeldBooking] = useState<Booking | undefined>();
  const [waitedOut, setWaitedOut] = useState(false);
  const expectingBooking = Boolean(bookingId) && !isReservationlessPractice;
  useEffect(() => {
    setWaitedOut(false);
    setHeldBooking(undefined);
  }, [slotId, bookingId, practiceSessionId]);
  useEffect(() => {
    if (resolvedBooking) {
      setHeldBooking(resolvedBooking);
    }
  }, [resolvedBooking]);
  const currentBooking = resolvedBooking ?? heldBooking;
  useEffect(() => {
    if (!expectingBooking || currentBooking || waitedOut) return undefined;
    const retry = setInterval(() => {
      if (typeof refetch === 'function') {
        refetch().catch(() => undefined);
      }
    }, 400);
    const timeout = setTimeout(() => setWaitedOut(true), 8000);
    return () => {
      clearInterval(retry);
      clearTimeout(timeout);
    };
  }, [currentBooking, expectingBooking, refetch, waitedOut]);

  const practiceBookingId = practiceSessionId ?? bookingId;

  // Memo on stable IDs, not the object reference — prevents re-requesting the token
  // on every react-query refetch that returns a structurally identical but new booking object.
  const liveKitJoin = useMemo(() => {
    if (!isFocused) return null;
    if (isReservationlessPractice && practiceCourseId && practiceModuleId && practiceTaskId && practiceBookingId) {
      return createLiveKitJoinHandle({
        courseId: practiceCourseId,
        moduleId: practiceModuleId,
        taskId: practiceTaskId,
        bookingId: practiceBookingId,
      });
    }
    if (!currentBooking || !isLiveKitBooking(currentBooking)) return null;
    return createLiveKitJoinHandle({
      courseId: currentBooking.courseId,
      moduleId: currentBooking.moduleId,
      taskId: currentBooking.taskId,
      bookingId: currentBooking.bookingId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentBooking?.bookingId,
    isFocused,
    isReservationlessPractice,
    practiceBookingId,
    practiceCourseId,
    practiceModuleId,
    practiceTaskId,
  ]);

  const practiceOralExamRouteIds = useMemo(() => {
    if (!practiceCourseId || !practiceModuleId || !practiceTaskId || !practiceBookingId) {
      return null;
    }
    return {
      courseId: practiceCourseId,
      moduleId: practiceModuleId,
      taskId: practiceTaskId,
      bookingId: practiceBookingId,
    };
  }, [practiceBookingId, practiceCourseId, practiceModuleId, practiceTaskId]);

  const bookingOralExamRouteIds = useMemo(() => {
    if (!currentBooking) return null;
    return {
      courseId: currentBooking.courseId,
      moduleId: currentBooking.moduleId,
      taskId: currentBooking.taskId,
      bookingId: currentBooking.bookingId,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable ids, not booking object identity
  }, [
    currentBooking?.bookingId,
    currentBooking?.courseId,
    currentBooking?.moduleId,
    currentBooking?.taskId,
  ]);

  const goToCourses = () => navigation.navigate('CourseScreen');

  // Leaving the exam should return the student to where they joined from (the
  // module/task screen that pushed this one) — not dump them on the generic
  // course list. Deep-link entries (`meeting/slot/:slotId`) have no history, so
  // fall back to the booking's module screen, then to courses.
  const leaveToOrigin = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.pop();
      return;
    }
    const originCourseId = currentBooking?.courseId ?? practiceCourseId;
    const originModuleId = currentBooking?.moduleId ?? practiceModuleId;
    if (originCourseId && originModuleId) {
      navigation.replace('ModuleScreen', {
        courseId: originCourseId,
        moduleId: originModuleId,
      });
      return;
    }
    navigation.navigate('CourseScreen');
  }, [navigation, currentBooking, practiceCourseId, practiceModuleId]);

  if (!userId) {
    return (
      <Center flex={1} testID="oral-exam-slot-auth-loading">
        <Spinner size="large" />
      </Center>
    );
  }

  if (
    !isReservationlessPractice
    && (bookingsLoading || (!currentBooking && expectingBooking && (isFetching || !waitedOut)))
  ) {
    return (
      <Center flex={1} testID="oral-exam-slot-loading">
        <Spinner size="large" />
        <Text size="sm" color="$textSubtle" mt="$2">{t('slotMeeting.loading')}</Text>
      </Center>
    );
  }

  if (isReservationlessPractice) {
    if (!isFocused) {
      return <Box flex={1} testID="oral-exam-practice-unfocused" />;
    }
    if (!liveKitJoin) {
      return (
        <Center flex={1} testID="oral-exam-practice-loading">
          <Spinner size="large" />
        </Center>
      );
    }
    return (
      <Box flex={1}>
        <OralExamLiveSession
          ids={practiceOralExamRouteIds!}
          liveKitJoin={liveKitJoin}
          onBack={leaveToOrigin}
          slotId={slotId}
        />
      </Box>
    );
  }

  if (!currentBooking) {
    return (
      <Box flex={1} p="$4">
        <VStack space="lg" testID="oral-exam-slot-no-booking">
          <Heading size="lg">{t('slotMeeting.title')}</Heading>
          <Text>{t('slotMeeting.noBooking')}</Text>
          <Button variant="outline" onPress={goToCourses}>
            <ButtonText>{t('slotMeeting.backToCourses')}</ButtonText>
          </Button>
        </VStack>
      </Box>
    );
  }

  if (!isLiveKitBooking(currentBooking)) {
    return (
      <Box flex={1} p="$4">
        <VStack space="lg" testID="oral-exam-slot-not-livekit">
          <Heading size="lg">{t('slotMeeting.title')}</Heading>
          <Text>{t('slotMeeting.notAvailable')}</Text>
          <Button variant="outline" onPress={goToCourses}>
            <ButtonText>{t('slotMeeting.backToCourses')}</ButtonText>
          </Button>
        </VStack>
      </Box>
    );
  }

  if (!isFocused) {
    return <Box flex={1} testID="oral-exam-slot-unfocused" />;
  }

  if (!liveKitJoin) {
    return (
      <Center flex={1} testID="oral-exam-slot-loading">
        <Spinner size="large" />
      </Center>
    );
  }

  return (
    <Box flex={1}>
      <OralExamLiveSession
        ids={bookingOralExamRouteIds!}
        liveKitJoin={liveKitJoin}
        onBack={leaveToOrigin}
        slotStartIso={currentBooking.startTime}
        slotId={currentBooking.slotId}
      />
    </Box>
  );
}

/**
 * Stack options for SlotMeetingScreen. The oral-exam meeting is a full-screen
 * experience whose single logo-left bar is `MeetingChrome` (rendered inside the
 * lobby), so the stack header is hidden to avoid a competing second bar. Back /
 * leave is surfaced inside `MeetingChrome` and the in-room control bar.
 */
export function slotMeetingScreenOptions() {
  return {
    headerShown: false,
    unmountOnBlur: true,
  };
}
